import type { ApiEnvelope } from '@trulyinbox/errors';
import { AnalyticsEvent, resetAnalytics, trackEvent } from './analytics';
import { env } from './env';
import { tokenStorage } from './storage-tokens';
import { useAuthStore } from './stores/auth';
import { clearTransientStores } from './clear-transient-stores';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    /** 6-digit business error code from the failure envelope; null for non-enveloped errors. */
    public readonly errorCode: string | null = null,
    public readonly data?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const BASE = env.apiUrl;

// Fallback only — used for NON-enveloped responses (binary, redirect proxies,
// pre-envelope edges). The enveloped path below reads `errorMessage` directly
// and never reaches here. Legacy Nest error bodies come in three shapes:
//   - string: `{ statusCode, message: 'EMAIL_ALREADY_CONNECTED' }`
//   - object: `{ statusCode, message: { error: 'DNS_CHECK_REQUIRED', spf, ... } }`
//   - array (ValidationPipe): `{ statusCode, message: ['email must be an email', ...] }`
// Falling back to "Request failed" for the latter two was hiding the actual cause.
function extractErrorMessage(body: Record<string, unknown> | null | undefined): string {
  const FALLBACK = 'Request failed';
  if (!body || typeof body !== 'object') return FALLBACK;

  const { message, error } = body as { message?: unknown; error?: unknown };

  if (typeof message === 'string' && message.length > 0) return message;

  if (Array.isArray(message)) {
    const first = message.find((m) => typeof m === 'string' && m.length > 0);
    if (typeof first === 'string') return first;
  }

  if (message && typeof message === 'object') {
    const inner = (message as { error?: unknown; message?: unknown });
    if (typeof inner.error === 'string' && inner.error.length > 0) return inner.error;
    if (typeof inner.message === 'string' && inner.message.length > 0) return inner.message;
  }

  // Top-level `error` is the auto-generated reason phrase ("Bad Request") for
  // string-message Nest throws, but for object-message throws it can carry the
  // real code. Only use it when it isn't a generic HTTP phrase.
  if (typeof error === 'string' && error.length > 0 && !/^[A-Z][a-z]/.test(error)) {
    return error;
  }

  return FALLBACK;
}

let onLogout: (() => void) | null = null;

export function setLogoutCallback(fn: () => void): void {
  onLogout = fn;
}

// Seamless re-auth (TRU-301). When the terminal 401 path fires (refresh token
// itself failed), we open an in-place re-auth modal via this callback instead
// of force-logging-out. Crucially `triggerReauth()` does NOT clear storage —
// the stale token stays so route guards / AppShell don't bounce to /login, and
// the modal can read the still-present user email + workspace.
let onReauthRequired: (() => void) | null = null;
let reauthPending = false;

export function setReauthCallback(fn: () => void): void {
  onReauthRequired = fn;
}

/** Cleared by the modal once re-auth succeeds, so refresh-and-retry resumes. */
export function resetReauthPending(): void {
  reauthPending = false;
}

function triggerReauth(): void {
  // No stored user means there's nothing to pre-fill and no workspace/route
  // worth preserving — fall back to the plain logout-and-redirect path.
  if (!tokenStorage.get('user') || !onReauthRequired) {
    forceLogout();
    return;
  }
  reauthPending = true;
  onReauthRequired();
}

// Fires only in OTHER tabs when localStorage changes (never in the tab that
// wrote it) — the browser-native signal for "something changed elsewhere."
// Once a DIFFERENT account's identity shows up here, this tab must stop
// silently working: every subsequent apiFetch call fails loudly instead of
// quietly serving (or being served) another account's data.
let sessionSupersededByOtherTab = false;

const SESSION_SUPERSEDED_MESSAGE =
  'Another account was signed in elsewhere in this browser. Reload this tab to continue.';

/** True for the synthetic 409 thrown below — never a real backend conflict. */
export function isSessionSupersededError(err: unknown): boolean {
  return (
    err instanceof ApiError &&
    err.status === 409 &&
    err.message === SESSION_SUPERSEDED_MESSAGE
  );
}

// Every query/mutation active in the tab hits this same synthetic error at
// once (and again on every later staleTime/focus refetch, since the flag
// above never resets) — without this gate the generic toast pipe in
// query-client.ts fires once per query, forever. Surface it exactly once;
// the fix is a reload, so there's nothing more a second toast would tell
// the user.
let sessionSupersededToastShown = false;

/** Returns true the first time it's called after a supersede; false after. */
export function consumeSessionSupersededToast(): boolean {
  if (sessionSupersededToastShown) return false;
  sessionSupersededToastShown = true;
  return true;
}

function parseUserId(raw: string | null): number | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { id?: unknown };
    return typeof parsed.id === 'number' ? parsed.id : null;
  } catch {
    return null;
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    // Only the 'user' key carries identity. A same-account token refresh
    // rewrites 'access_token'/'refresh_token' too, but NOT with a different
    // user.id, so gating on 'user' — and comparing ids, not raw values —
    // is what keeps normal refreshes and workspace switches from tripping
    // this. A null newValue means the OTHER tab signed out (key removed);
    // that's deliberately not a trigger here (see plan §1 non-goals) — only
    // a genuinely different, non-null identity is.
    if (event.key !== 'user' || event.storageArea !== localStorage) return;
    const incomingUserId = parseUserId(event.newValue);
    if (incomingUserId === null) return;

    const myUserId = useAuthStore.getState().user?.id ?? null;
    if (myUserId !== null && incomingUserId !== myUserId) {
      sessionSupersededByOtherTab = true;
    }
  });
}

let isRefreshing = false;
let refreshQueue: Array<(token: string | null) => void> = [];

function notifyQueue(token: string | null): void {
  refreshQueue.forEach((resolve) => resolve(token));
  refreshQueue = [];
}

async function attemptTokenRefresh(): Promise<string | null> {
  // Re-auth modal is already up — don't hammer /auth/refresh with the burst of
  // background refetches that keep 401-ing behind the modal.
  if (reauthPending) return null;

  const refreshToken = tokenStorage.get('refresh_token');
  if (!refreshToken) return null;

  if (isRefreshing) {
    return new Promise((resolve) => refreshQueue.push(resolve));
  }

  isRefreshing = true;
  try {
    const res = await fetch(`${BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });

    if (!res.ok) {
      notifyQueue(null);
      return null;
    }

    const data = await res.json() as { accessToken: string; refreshToken: string };
    tokenStorage.set('access_token', data.accessToken);
    tokenStorage.set('refresh_token', data.refreshToken);
    notifyQueue(data.accessToken);
    return data.accessToken;
  } catch {
    notifyQueue(null);
    return null;
  } finally {
    isRefreshing = false;
  }
}

function forceLogout(): void {
  // Same event the voluntary `signOut` sends, and for the same reason BEFORE
  // `resetAnalytics()` below: it must be attributed to the user being logged
  // out. Only while a session still exists — a burst of concurrent 401s each
  // lands here, and every call after the first finds the store already clear.
  if (useAuthStore.getState().accessToken) trackEvent(AnalyticsEvent.SignedOut);
  // Clear BOTH the persisted localStorage keys AND the in-memory Zustand auth
  // store. The route guards (`requireAuth` / `requireGuest` in
  // `lib/route-guards.ts`) and the AppShell redirect effect read auth state
  // from `useAuthStore.getState()`, NOT from localStorage. Clearing only
  // localStorage (via `tokenStorage.clearAll()`) leaves the store's in-memory
  // `accessToken` set, so `requireGuest` on `/login` still sees the user as
  // authenticated and bounces the logout redirect straight back to
  // `/email-accounts`, whose loader refetches with the now-missing token, 401s,
  // and re-enters `forceLogout` — an infinite redirect + refetch loop that only
  // a hard refresh (which rehydrates the store from the emptied localStorage)
  // could break. `clear()` resets the store; its persist middleware also
  // removes the localStorage keys, and the explicit `clearAll()` covers the
  // non-persisted edge.
  tokenStorage.clearAll();
  useAuthStore.getState().clear();
  // Match the voluntary logout path (`clearAuth` → `resetAnalytics()`): a
  // forced/terminal logout must also detach the analytics identity, else events
  // between here and the next sign-in stay bound to the previous user.
  resetAnalytics();
  // Match `clearAuth`'s reset of the transient per-session Zustand stores
  // (TRU-1039) via the shared `clearTransientStores` helper.
  clearTransientStores();
  onLogout?.();
}

// When the API auto-swaps a soft-deleted (or no-longer-a-member) workspace, it
// returns the rebuilt access token in this header, plus the new workspace's
// {id, name} in a sibling header. We swap both into storage and reload so the
// UI (sidebar workspace name, switcher "active" check, cached queries, etc.)
// lines up with the JWT's new workspaceId — writing only the token here left
// the `workspace` object in the auth store pointed at the deleted workspace
// forever, since nothing else in the app ever re-derives it (TRU-987).
const REFRESHED_TOKEN_HEADER = 'x-refreshed-access-token';
const REFRESHED_WORKSPACE_HEADER = 'x-refreshed-workspace';

function reloadAfterTokenRefresh(res: Response): void {
  const refreshed = res.headers.get(REFRESHED_TOKEN_HEADER);
  if (!refreshed || typeof window === 'undefined') return;
  tokenStorage.set('access_token', refreshed);

  const refreshedWorkspace = res.headers.get(REFRESHED_WORKSPACE_HEADER);
  if (refreshedWorkspace) {
    try {
      // Written in the same shape `writeJsonOrRemove` uses in stores/auth.ts,
      // so the Zustand persist adapter rehydrates it as `AuthWorkspace` on the
      // reload below.
      const workspace = JSON.parse(decodeURIComponent(refreshedWorkspace)) as {
        id: number;
        name: string;
      };
      tokenStorage.set('workspace', JSON.stringify(workspace));
    } catch {
      // Malformed header — worst case the workspace name stays stale until the
      // user switches manually; don't block the token refresh over it.
    }
  }

  setTimeout(() => window.location.reload(), 0);
}

export async function apiFetch<T>(
  path: string,
  init?: RequestInit & { auth?: boolean },
): Promise<T> {
  if (sessionSupersededByOtherTab && init?.auth !== false) {
    throw new ApiError(409, SESSION_SUPERSEDED_MESSAGE);
  }

  const buildHeaders = (token?: string | null): Record<string, string> => ({
    ...(init?.body != null ? { 'Content-Type': 'application/json' } : {}),
    ...(init?.headers as Record<string, string>),
    ...(init?.auth !== false && token ? { Authorization: `Bearer ${token}` } : {}),
  });

  const storedToken =
    init?.auth !== false ? tokenStorage.get('access_token') : null;

  const { auth: _, ...fetchInit } = init ?? {};

  let res = await fetch(`${BASE}${path}`, {
    ...fetchInit,
    headers: buildHeaders(storedToken),
  });

  if (res.status === 401 && init?.auth !== false) {
    const newToken = await attemptTokenRefresh();

    if (!newToken) {
      triggerReauth();
      throw new ApiError(401, 'Session expired');
    }

    res = await fetch(`${BASE}${path}`, {
      ...fetchInit,
      headers: buildHeaders(newToken),
    });

    if (res.status === 401) {
      triggerReauth();
      throw new ApiError(401, 'Session expired');
    }
  }

  reloadAfterTokenRefresh(res);

  if (res.status === 204) return undefined as T;

  const envelope = (await res.json().catch(() => null)) as ApiEnvelope<T> | null;

  // Enveloped path — every api/migrated endpoint returns the uniform envelope.
  if (envelope && typeof envelope === 'object' && 'success' in envelope) {
    if (envelope.success) return envelope.data;
    throw new ApiError(res.status, envelope.errorMessage, envelope.errorCode, envelope);
  }

  // Fallback — non-enveloped/exempt response (binary, redirect proxy, pre-migration edge).
  if (!res.ok) {
    const body = (envelope ?? {}) as Record<string, unknown>;
    throw new ApiError(res.status, extractErrorMessage(body), null, body);
  }
  return envelope as T;
}
